// Drives the WebAuthn flows: passkey sign-in (via the explicit button)
// and passkey enrollment. Handed an options blob from the server
// (Object) and a form to submit through (Target).
//
// Note: no conditional UI (mediation: "conditional"). It auto-offered a
// saved passkey on field focus, but Chrome re-rendered its native
// autofill popup on every keystroke — a flashing "Use Passkey from
// Another Device" tooltip (#2561). Sign-in goes through the button.
import { Controller } from "@hotwired/stimulus"
import { create, get } from "@github/webauthn-json"

export default class extends Controller {
  static values = {
    authenticationOptions: { type: Object, default: {} },
    registrationOptions:   { type: Object, default: {} }
  }

  static targets = ["form"]

  async signIn(event) {
    event?.preventDefault()
    try {
      const credential = await get({ publicKey: this.authenticationOptionsValue })
      this.#fillFormAndSubmit(credential)
    } catch (err) {
      // User cancelled the prompt, or no passkey available.
      console.warn("[passkey] sign-in cancelled or failed:", err.message)
    }
  }

  async enroll(event) {
    event?.preventDefault()
    try {
      const credential = await create({ publicKey: this.registrationOptionsValue })
      this.#fillFormAndSubmit(credential)
    } catch (err) {
      console.warn("[passkey] enrollment cancelled or failed:", err.message)
    }
  }

  #fillFormAndSubmit(credential) {
    if (!this.hasFormTarget) return
    this.#flattenInto(this.formTarget, "passkey", credential)
    this.formTarget.submit()
  }

  // Recursively flatten a nested credential object into `name`-style
  // form params (e.g. `passkey[response][clientDataJSON]`). Arrays
  // (such as `response.transports` on registration) are written as
  // multiple `name[]` inputs so Rack/Rails parse them back into an
  // Array — coercing to a CSV string would silently malform the
  // payload.
  #flattenInto(form, prefix, value) {
    if (value === null || value === undefined) {
      this.#setHidden(form, prefix, "")
    } else if (Array.isArray(value)) {
      // Drop any prior items first so a re-fill doesn't accumulate
      // stale entries across submissions.
      form.querySelectorAll(`input[name="${CSS.escape(`${prefix}[]`)}"]`).forEach(i => i.remove())
      for (const item of value) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = `${prefix}[]`
        input.value = item
        form.appendChild(input)
      }
    } else if (typeof value === "object") {
      for (const [k, v] of Object.entries(value)) {
        this.#flattenInto(form, `${prefix}[${k}]`, v)
      }
    } else {
      this.#setHidden(form, prefix, value)
    }
  }

  #setHidden(form, name, value) {
    let input = form.querySelector(`input[name="${CSS.escape(name)}"]`)
    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      form.appendChild(input)
    }
    input.value = value
  }
}
